import { Link, getRouteApi } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { pickDefaultSplitAgeGroup } from "@/lib/split-age-group";
import { NavBreadcrumbs } from "@/components/NavBreadcrumbs";
import {
  AgeGroupParticipationChart,
  type AgeGroupParticipationDatum,
} from "@/components/charts/AgeGroupParticipationChart";
import {
  SplitDistributionChart,
  type SplitDistributionRecord,
} from "@/components/charts/SplitDistributionChart";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import "./events.series.$seriesId.css";

const routeApi = getRouteApi("/events/series/$seriesId");

function formatYearLabel(year: number | null): string {
  return year ? String(year) : "Unknown Year";
}

function isSplitDistributionRecord(
  value: unknown,
): value is SplitDistributionRecord {
  if (!value || typeof value !== "object") return false;
  const candidate = value as {
    distribution?: { buckets?: unknown };
  };
  return Array.isArray(candidate.distribution?.buckets);
}

function formatSeriesWindow(
  firstYear: number | null | undefined,
  lastYear: number | null | undefined,
): string | null {
  if (firstYear == null && lastYear == null) return null;
  const start = firstYear ?? lastYear;
  const end = lastYear ?? firstYear;
  if (start == null || end == null) return null;
  return start === end ? String(start) : `${start}-${end}`;
}

function formatSegmentTypeLabel(segmentType: string): string {
  if (!segmentType) return "Split";
  return segmentType.charAt(0).toUpperCase() + segmentType.slice(1);
}

type ParticipationMetric = "participants" | "finishers" | "dnf" | "dns" | "dq";

type SeriesYearEntry = {
  eventId: string;
  eventName: string;
  year: number | null;
  eventDate: string | null;
  resultCount: number;
  fastestTimeFormatted: string | null;
};

type SeriesRecordHighlight = {
  resultId: string;
  athleteName: string;
  year?: number | null;
  timeFormatted: string;
};

const participationMetricOptions: {
  value: ParticipationMetric;
  label: string;
}[] = [
  { value: "participants", label: "Participants" },
  { value: "finishers", label: "Finishers" },
  { value: "dnf", label: "DNF" },
  { value: "dns", label: "DNS" },
  { value: "dq", label: "DQ" },
];

const participationChartConfig = {
  participants: {
    label: "Participants",
    color: "var(--d8-cyan)",
  },
  finishers: {
    label: "Finishers",
    color: "#8e7cff",
  },
  dnf: {
    label: "DNF",
    color: "var(--d8-pink)",
  },
  dns: {
    label: "DNS",
    color: "#7dd3fc",
  },
  dq: {
    label: "DQ",
    color: "#f59e0b",
  },
} satisfies ChartConfig;

function EventSeriesSummaryCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <article className="event-series-summary-card">
      <p className="event-series-summary-label">{label}</p>
      <p className="event-series-summary-value">{value}</p>
      {detail ? <p className="event-series-summary-detail">{detail}</p> : null}
    </article>
  );
}

function EventSeriesRecordCard({
  label,
  record,
  tone = "default",
}: {
  label: string;
  record: SeriesRecordHighlight | null;
  tone?: "default" | "overall" | "male" | "female" | "split";
}) {
  return (
    <article
      className={`event-series-record-card event-series-record-card--${tone}`}
    >
      <p className="event-series-record-label">{label}</p>
      {record ? (
        <>
          <p className="event-series-record-value">{record.timeFormatted}</p>
          <div className="event-series-record-meta">
            <Link
              to="/results/$resultId"
              params={{ resultId: record.resultId }}
              className="event-series-record-link"
            >
              {record.athleteName}
            </Link>
            {record.year ? (
              <span className="event-series-record-year">{record.year}</span>
            ) : null}
          </div>
        </>
      ) : (
        <p className="event-series-record-empty">No data</p>
      )}
    </article>
  );
}

export default function EventSeriesPage() {
  const { seriesId } = routeApi.useParams();
  const { year: searchYear } = routeApi.useSearch();
  const [splitAgeGroup, setSplitAgeGroup] = useState("");
  const [participationMetric, setParticipationMetric] =
    useState<ParticipationMetric>("participants");
  const selectedParticipationMetric = participationMetricOptions.find(
    (option) => option.value === participationMetric,
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ["event-series", seriesId],
    queryFn: async () => {
      const { data, error } = await api.GET("/events/series/{seriesId}", {
        params: {
          path: { seriesId },
        },
      });
      if (error) throw error;
      return data;
    },
  });

  const { data: ageGroupSplitsData, isFetching: ageGroupSplitsFetching } =
    useQuery({
      queryKey: ["series-age-group-splits", seriesId, splitAgeGroup],
      queryFn: async () => {
        const { data, error } = await api.GET(
          "/events/series/{seriesId}/splits",
          {
            params: {
              path: { seriesId },
              query: { ageGroup: splitAgeGroup },
            },
          },
        );
        if (error) throw error;
        return data;
      },
      enabled: !!splitAgeGroup,
    });

  const seriesAgeGroups = useMemo(
    () =>
      [
        ...new Set(
          (
            (
              data?.stats as
                | {
                    ageGroupParticipationByGender?: Array<{
                      ageGroup: string;
                    }>;
                  }
                | undefined
            )?.ageGroupParticipationByGender ?? []
          )
            .map((row) => row.ageGroup)
            .filter((ageGroup): ageGroup is string => ageGroup.length > 0),
        ),
      ],
    [data?.stats],
  );

  const splitDistributionSplits = (
    (ageGroupSplitsData?.splits ?? []) as unknown[]
  ).filter(isSplitDistributionRecord);

  const canonicalKnownYears = useMemo(() => {
    const yearsByNumber = new Map<number, SeriesYearEntry>();

    for (const yearItem of data?.years ?? []) {
      if (yearItem.year == null) continue;

      const existing = yearsByNumber.get(yearItem.year);
      if (!existing) {
        yearsByNumber.set(yearItem.year, yearItem);
        continue;
      }

      const existingDate = existing.eventDate
        ? new Date(existing.eventDate).getTime()
        : Number.NEGATIVE_INFINITY;
      const nextDate = yearItem.eventDate
        ? new Date(yearItem.eventDate).getTime()
        : Number.NEGATIVE_INFINITY;

      if (nextDate > existingDate) {
        yearsByNumber.set(yearItem.year, yearItem);
      }
    }

    return [...yearsByNumber.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([year, item]) => ({ ...item, year }));
  }, [data?.years]);

  const knownYearsDesc = useMemo(
    () => canonicalKnownYears.map((item) => item.year),
    [canonicalKnownYears],
  );
  const yearsTracked = canonicalKnownYears.length;
  const latestKnownYear = knownYearsDesc[0];
  const earliestKnownYear =
    knownYearsDesc.length > 0 ? knownYearsDesc[knownYearsDesc.length - 1] : null;
  const selectedYear =
    searchYear != null && knownYearsDesc.includes(searchYear)
      ? searchYear
      : latestKnownYear;
  const requestedYearUnavailable =
    searchYear != null && !knownYearsDesc.includes(searchYear);
  const hasKnownYears = canonicalKnownYears.length > 0;

  const splitAgeGroupCounts = useMemo(
    () =>
      (
        (
          data?.stats as
            | {
                ageGroupParticipationByGender?: Array<{
                  ageGroup: string;
                  maleCount: number;
                  femaleCount: number;
                }>;
              }
            | undefined
        )?.ageGroupParticipationByGender ?? []
      ).map((row) => ({
        ageGroup: row.ageGroup,
        count: row.maleCount + row.femaleCount,
      })),
    [data?.stats],
  );

  const trendData = useMemo(
    () =>
      [...(data?.stats.yearlyParticipationTrend ?? [])]
        .sort((a, b) => {
          if (a.year == null && b.year == null) return 0;
          if (a.year == null) return 1;
          if (b.year == null) return -1;
          return a.year - b.year;
        })
        .map((item) => ({
          ...item,
          yearLabel: formatYearLabel(item.year),
        })),
    [data?.stats.yearlyParticipationTrend],
  );

  const hasMultipleKnownTrendYears = useMemo(() => {
    const years = new Set(
      trendData
        .map((item) => item.year)
        .filter((year): year is number => year != null),
    );
    return years.size > 1;
  }, [trendData]);

  const ageGroupParticipationData: AgeGroupParticipationDatum[] = (
    (
      data?.stats as
        | {
            ageGroupParticipationByGender?: Array<{
              ageGroup: string;
              maleCount: number;
              femaleCount: number;
            }>;
          }
        | undefined
    )?.ageGroupParticipationByGender ?? []
  ).map((row) => ({
    ageGroup: row.ageGroup,
    maleCount: row.maleCount,
    femaleCount: row.femaleCount,
  }));

  const seriesMaleParticipants = ageGroupParticipationData.reduce(
    (sum, row) => sum + row.maleCount,
    0,
  );
  const seriesFemaleParticipants = ageGroupParticipationData.reduce(
    (sum, row) => sum + row.femaleCount,
    0,
  );

  const totalTrackedResults = useMemo(
    () =>
      (data?.years ?? []).reduce(
        (sum, yearItem) => sum + (yearItem.resultCount || 0),
        0,
      ),
    [data?.years],
  );
  const latestYearResultCount = useMemo(() => {
    if (latestKnownYear == null) return null;

    return (data?.years ?? [])
      .filter((yearItem) => yearItem.year === latestKnownYear)
      .reduce((sum, yearItem) => sum + (yearItem.resultCount || 0), 0);
  }, [data?.years, latestKnownYear]);

  const seriesWindow = formatSeriesWindow(
    data?.series.firstYear ?? earliestKnownYear,
    data?.series.lastYear ?? latestKnownYear,
  );
  const seriesCourseProfile = (data as typeof data & { courseProfile?: { swim?: string | null; bike?: string | null; run?: string | null } | null })?.courseProfile ?? null;

  const seriesMeta = [data?.series.brand, data?.series.raceType, seriesWindow]
    .filter(Boolean)
    .join(" · ");
  const latestYearRecord = canonicalKnownYears.find(
    (yearItem) => yearItem.year === latestKnownYear,
  );

  useEffect(() => {
    setSplitAgeGroup("");
  }, [seriesId]);

  useEffect(() => {
    if (splitAgeGroup) return;
    if (!data) return;
    if (seriesAgeGroups.length === 0) return;

    const defaultAgeGroup = pickDefaultSplitAgeGroup(
      seriesAgeGroups,
      splitAgeGroupCounts,
    );

    if (defaultAgeGroup) {
      setSplitAgeGroup(defaultAgeGroup);
    }
  }, [splitAgeGroup, data, seriesAgeGroups, splitAgeGroupCounts]);

  if (isLoading) {
    return (
      <div className="event-series-page">
        <div className="event-series-hero animate-pulse">
          <div className="mb-3 h-8 w-1/3 rounded bg-muted/70"></div>
          <div className="h-4 w-1/4 rounded bg-muted/50"></div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="event-series-page">
        <NavBreadcrumbs
          items={[{ label: "Records", to: "/records", search: {} }]}
          current="Event Series"
        />
        <p className="event-series-error">
          {error instanceof Error
            ? error.message
            : "Error loading event series"}
        </p>
      </div>
    );
  }

  return (
    <div className="event-series-page">
      <header className="event-series-hero">
        <NavBreadcrumbs
          items={[
            { label: "Records", to: "/records", search: {} },
            ...(data.group
              ? [
                  {
                    label: data.group.displayName,
                    to: "/events/group/$groupId",
                    params: { groupId: data.group.id },
                    search: {},
                  },
                ]
              : []),
          ]}
          current={data.series.displayName}
        />
        <div className="event-series-hero-copy">
          <p className="event-series-eyebrow">Event Series</p>
          <h1 className="event-series-title">{data.series.displayName}</h1>
          {seriesMeta ? <p className="event-series-meta">{seriesMeta}</p> : null}
          {seriesCourseProfile ? (
            <div className="event-series-course-profile">
              {seriesCourseProfile.swim ? (
                <span className="event-series-course-pill event-series-course-pill--swim">
                  Swim: {seriesCourseProfile.swim}
                </span>
              ) : null}
              {seriesCourseProfile.bike ? (
                <span className="event-series-course-pill event-series-course-pill--bike">
                  Bike: {seriesCourseProfile.bike}
                </span>
              ) : null}
              {seriesCourseProfile.run ? (
                <span className="event-series-course-pill event-series-course-pill--run">
                  Run: {seriesCourseProfile.run}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="event-series-summary-grid">
          <EventSeriesSummaryCard
            label="Years tracked"
            value={yearsTracked.toLocaleString()}
            detail={seriesWindow ?? "Series window unavailable"}
          />
          <EventSeriesSummaryCard
            label="Total results"
            value={totalTrackedResults.toLocaleString()}
            detail={
              latestYearResultCount != null
                ? `${latestYearResultCount.toLocaleString()} in latest year`
                : "Result history across the series"
            }
          />
          <EventSeriesSummaryCard
            label="Latest results"
            value={latestKnownYear != null ? String(latestKnownYear) : "Pending"}
            detail={
              latestYearRecord?.fastestTimeFormatted
                ? `Fastest ${latestYearRecord.fastestTimeFormatted}`
                : "Waiting on a completed event"
            }
          />
        </div>
      </header>

      <section className="event-series-section" aria-label="Year Explorer">
        <div className="event-series-section-header">
          <div>
            <h2 className="event-series-section-title">Year Explorer</h2>
            <p className="event-series-section-copy">
              Jump into the event page for a specific season without losing the
              series context.
            </p>
          </div>
        </div>

        {hasKnownYears ? (
          <>
            <div className="event-series-year-shell sticky top-0 z-20">
              <nav
                aria-label="Browse years in this event series"
                className="event-series-year-nav"
              >
                <div className="event-series-year-nav-header">
                  <p className="event-series-year-nav-note">
                    Latest year with results:{" "}
                    <span>{latestKnownYear ?? "Unknown"}</span>
                  </p>
                </div>
                <div className="d8-scrollable min-h-14">
                  <div className="event-series-year-strip">
                    {canonicalKnownYears.map((yearItem) => {
                      const isActive = yearItem.year === selectedYear;
                      const isFuture =
                        yearItem.eventDate != null &&
                        new Date(`${yearItem.eventDate}T00:00:00`) > new Date();

                      return (
                        <Link
                          key={yearItem.eventId}
                          to="/events/$eventId"
                          params={{ eventId: yearItem.eventId }}
                          search={{
                            ageGroup: undefined,
                            highlight: undefined,
                            highlightRank: undefined,
                            page: undefined,
                          }}
                          aria-label={`Open ${yearItem.year} event results`}
                          className={`event-series-year-link${isActive ? " is-active" : ""}`}
                        >
                          <span className="event-series-year-value">
                            {yearItem.year}
                          </span>
                          <span className="event-series-year-detail">
                            {isFuture
                              ? "Upcoming"
                              : `${yearItem.resultCount.toLocaleString()} participants`}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </nav>
            </div>

            {requestedYearUnavailable && selectedYear != null ? (
              <div className="event-series-inline-notice">
                Year {searchYear} is not available for this series. Using{" "}
                {selectedYear} as the quick-link year.
              </div>
            ) : null}
          </>
        ) : (
          <div className="event-series-empty-state">
            <p className="event-series-empty-copy">
              No known year data is available for this series yet.
            </p>
          </div>
        )}
      </section>

      <section className="event-series-section">
        <div className="event-series-section-header">
          <div>
            <h2 className="event-series-section-title">Series Records</h2>
            <p className="event-series-section-copy">
              All-time benchmarks across every edition of the series.
            </p>
          </div>
        </div>

        <div className="event-series-record-grid">
          {data.stats.fastestOverall ? (
            <EventSeriesRecordCard
              label="Fastest Overall"
              tone="overall"
              record={data.stats.fastestOverall}
            />
          ) : null}
          {data.stats.fastestByGender.male ? (
            <EventSeriesRecordCard
              label="Fastest Male"
              tone="male"
              record={data.stats.fastestByGender.male}
            />
          ) : null}
          {data.stats.fastestByGender.female ? (
            <EventSeriesRecordCard
              label="Fastest Female"
              tone="female"
              record={data.stats.fastestByGender.female}
            />
          ) : null}
        </div>

        {data.stats.splitRecords.length > 0 ? (
          <div className="event-series-record-grid event-series-record-grid--splits">
            {data.stats.splitRecords.map((split) => (
              <EventSeriesRecordCard
                key={split.segmentType}
                label={`Fastest ${formatSegmentTypeLabel(split.segmentType)}`}
                tone="split"
                record={split.fastest}
              />
            ))}
          </div>
        ) : null}
      </section>

      <section className="event-series-section">
        <div className="event-series-section-header">
          <div>
            <h2 className="event-series-section-title">Stats</h2>
            <p className="event-series-section-copy">
              Participation, pacing distribution, and historical trends for the
              full series.
            </p>
          </div>
        </div>

        {seriesAgeGroups.length > 0 ? (
          <div className="event-series-chart-card">
            <SplitDistributionChart
              ageGroups={seriesAgeGroups}
              selectedAgeGroup={splitAgeGroup}
              onAgeGroupChange={setSplitAgeGroup}
              splits={splitDistributionSplits}
              isFetching={ageGroupSplitsFetching}
              title={splitDistributionSplits.some((s) => s.segmentType === "finish") ? "Finish Time Distribution" : "Age Group Split Distribution"}
              description={splitDistributionSplits.some((s) => s.segmentType === "finish") ? "Compare all-time finish time distributions across the series for one age group." : "Compare all-time split distributions across the series for one age group."}
            />
          </div>
        ) : null}

        <div className="event-series-chart-card">
          <div className="event-series-panel-header">
            <div>
              <h3 className="event-series-panel-title">Participation Totals</h3>
              <p className="event-series-panel-copy">
                Overall counts recorded across this series.
              </p>
            </div>
            <p className="event-series-panel-kicker">
              Men {seriesMaleParticipants.toLocaleString()} · Women{" "}
              {seriesFemaleParticipants.toLocaleString()}
            </p>
          </div>
          <div className="event-series-totals-grid">
            <div className="event-series-total-card">
              <p className="event-series-total-value">
                {(
                  data.stats.participationTotals as {
                    participants?: number;
                  }
                ).participants?.toLocaleString() ?? totalTrackedResults.toLocaleString()}
              </p>
              <p className="event-series-total-label">Participants</p>
            </div>
            <div className="event-series-total-card">
              <p className="event-series-total-value">
                {data.stats.participationTotals.finishers.toLocaleString()}
              </p>
              <p className="event-series-total-label">Finishers</p>
            </div>
            <div className="event-series-total-card">
              <p className="event-series-total-value">
                {data.stats.participationTotals.dnf.toLocaleString()}
              </p>
              <p className="event-series-total-label">DNF</p>
            </div>
            <div className="event-series-total-card">
              <p className="event-series-total-value">
                {data.stats.participationTotals.dns.toLocaleString()}
              </p>
              <p className="event-series-total-label">DNS</p>
            </div>
            <div className="event-series-total-card">
              <p className="event-series-total-value">
                {data.stats.participationTotals.dq.toLocaleString()}
              </p>
              <p className="event-series-total-label">DQ</p>
            </div>
          </div>
        </div>

        {hasMultipleKnownTrendYears ? (
          <div className="event-series-chart-stack">
            <div className="event-series-chart-card">
              <div className="event-series-panel-header event-series-panel-header--with-control">
                <div>
                  <h3 className="event-series-panel-title">
                    Yearly Participation Trend
                  </h3>
                  <p className="event-series-panel-copy">
                    Track how field size changes from season to season.
                  </p>
                </div>
                <div className="event-series-chart-control">
                  <label
                    htmlFor="participation-metric"
                    className="event-series-control-label"
                  >
                    Metric
                  </label>
                  <Combobox
                    items={participationMetricOptions}
                    value={selectedParticipationMetric ?? null}
                    onValueChange={(value) =>
                      setParticipationMetric(
                        (value?.value as ParticipationMetric | undefined) ??
                          "participants",
                      )
                    }
                  >
                    <ComboboxInput
                      id="participation-metric"
                      aria-label="Select participation trend metric"
                      className="w-[180px]"
                    />
                    <ComboboxContent>
                      <ComboboxEmpty>No metrics found.</ComboboxEmpty>
                      <ComboboxList>
                        {(option) => (
                          <ComboboxItem key={option.value} value={option}>
                            {option.label}
                          </ComboboxItem>
                        )}
                      </ComboboxList>
                    </ComboboxContent>
                  </Combobox>
                </div>
              </div>

              <ChartContainer
                config={participationChartConfig}
                className="min-h-[260px]"
              >
                <LineChart
                  data={trendData}
                  margin={{ top: 8, right: 12, left: 4, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="yearLabel"
                    tickLine={false}
                    axisLine={false}
                    minTickGap={18}
                  />
                  <YAxis
                    type="number"
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    width={50}
                  />
                  <ChartTooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    content={
                      <ChartTooltipContent
                        labelFormatter={(_label, payload) =>
                          `Year ${(payload?.[0]?.payload as { yearLabel?: string } | undefined)?.yearLabel ?? "Unknown Year"}`
                        }
                        formatter={(value, name) =>
                          `${Number(value ?? 0).toLocaleString()} ${String(name)}`
                        }
                      />
                    }
                  />
                  <Line
                    dataKey={participationMetric}
                    name={
                      participationMetricOptions.find(
                        (option) => option.value === participationMetric,
                      )?.label ?? participationMetric
                    }
                    type="monotone"
                    stroke={`var(--color-${participationMetric})`}
                    strokeWidth={2.5}
                    dot={{
                      r: 3.5,
                      fill: `var(--color-${participationMetric})`,
                    }}
                    activeDot={{
                      r: 5,
                      fill: `var(--color-${participationMetric})`,
                      strokeWidth: 2,
                    }}
                  />
                </LineChart>
              </ChartContainer>
            </div>

            <div className="event-series-chart-card">
              <AgeGroupParticipationChart
                data={ageGroupParticipationData}
                titleClassName="event-series-panel-title"
                className="event-series-age-group-chart"
                description="Compare the participant mix across male and female age groups."
                withInnerChartBorder
              />
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
